import * as React from 'react';
import { useDispatch } from 'react-redux';
import * as _ from 'lodash';
import { Redirect } from 'react-router-dom';
import {
  FileType,
  Logging,
  Notification,
  NotificationData,
  getUuidV4,
  API,
  BulkUploadRequest,
  BulkUploadResponse,
  ResponseError,
  BulkUploadFile,
  formatFileSize,
  DropZoneProps,
  DropZone,
  VertexLink,
} from '@vcd-ecm-ui/Common';
import { VtxButton, VtxIcon } from '@vertexinc/vtx-ui-react-component-library';
import BulkUploadProps from './BulkUploadProps.interface';
import '../bulk-upload.scss';

const BYTES_PER_MEGABYTE = 1048576;
const BYTES_PER_GIGABYTE = BYTES_PER_MEGABYTE * 1024;

const ECM_UPLOAD_TEMPLATE = '/template/Vertex Exemption Certificate Manager Bulk Upload Template.csv';
const ECM_UPLOAD_REFERENCE = '/template/Vertex Exemption Certificate Manager Bulk Upload Reference.xlsx';

const BulkUpload = ({
  gatewayEndPoint,
  username,
  numberUploads,
  maxNumberUploads,
  uploadFilesToS3,
}: BulkUploadProps): JSX.Element => {
  const [leftDropFiles, setLeftDropFiles] = React.useState([]);
  const [rightDropFiles, setRightDropFiles] = React.useState([]);
  const [leftStatusMap, setLeftStatusMap] = React.useState(new Map<string, boolean | string>());
  const [rightStatusMap, setRightStatusMap] = React.useState(new Map<string, boolean | string>());
  const [redirect, setRedirect] = React.useState(null);
  const [notification, setNotification] = React.useState(null);
  const [submitting, setSubmitting] = React.useState(false);
  const [leftFilesError, setLeftFilesError] = React.useState(null);
  const [rightFilesError, setRightFilesError] = React.useState(null);

  const dispatch = useDispatch();

  const onNotificationClose = (): void => {
    setNotification(null);
  };

  // Reset the Bulk Uploader
  const onClearAllClick = (): void => {
    // Reset the Bulk Uploader
    setLeftDropFiles([]);
    setRightDropFiles([]);
    setLeftStatusMap(new Map<string, boolean | string>());
    setRightStatusMap(new Map<string, boolean | string>());
    setLeftFilesError(null);
    setRightFilesError(null);
  };

  // Get the Files Size
  const getFilesSize = (files: File[]): number => {
    let totalSize = 0;

    files.forEach((file: File) => {
      totalSize += file.size;
    });

    return totalSize;
  };

  // Get the Valid Files
  const getValidFiles = (files: File[], statusMap: Map<string, boolean | string>): File[] => {
    const validFiles: File[] = [];

    files.forEach((file: File) => {
      const data: boolean | string = statusMap.get(file.name);

      if (typeof (data) === 'boolean') {
        validFiles.push(file);
      }
    });

    return validFiles;
  };

  // Get the Invalid Files
  const getInvalidFiles = (files: File[], statusMap: Map<string, boolean | string>): File[] => {
    const invalidFiles: File[] = [];

    files.forEach((file: File) => {
      const data: boolean | string = statusMap.get(file.name);

      if (typeof (data) === 'string') {
        invalidFiles.push(file);
      }
    });

    return invalidFiles;
  };

  const submitBulkUpload = (leftFiles: File[], rightFiles: File[]) => async (): Promise<void> => {
    try {
      const api = new API(gatewayEndPoint);

      // Create the Bulk Upload Request
      const bulkUploadRequest: BulkUploadRequest = {
        username,
        certificateCsvFile: {
          name: leftFiles[0].name,
          contentType: leftFiles[0].type,
        },
        // eslint-disable-next-line arrow-body-style
        certificateImageFiles: rightFiles.map((file: File) => {
          return {
            name: file.name,
            contentType: file.type,
          };
        }),
      };

      const fileNameUrlMap: Map<string, string> = new Map<string, string>();

      // Submit the Bulk Upload Request
      const bulkUploadResponse: BulkUploadResponse = await api.bulkupload.submitBulkUploadV4(bulkUploadRequest);

      Logging.Info('Submitted Bulk Upload:', bulkUploadResponse);

      // Extract the Url for the CSV
      fileNameUrlMap.set(bulkUploadResponse.certificateCsvFile.name, bulkUploadResponse.certificateCsvFile.url);

      // Extract the Url for the Images
      bulkUploadResponse.certificateImageFiles.forEach((file: BulkUploadFile) => {
        fileNameUrlMap.set(file.name, file.url);
      });

      const { uuid } = bulkUploadResponse;

      // Add the Left Drop Files
      let files: File[] = leftFiles;

      // Add the Right Drop Files
      files = files.concat(rightFiles);

      // Upload the Files to S3
      uploadFilesToS3(uuid, files, fileNameUrlMap);

      // Direct to Bulk Upload History
      setRedirect('/bulkupload-history');
    } catch (error) {
      const notificationData: NotificationData = {
        type: 'Error',
        title: 'Bulk Upload Submit Error',
        description: 'Unable to submit the Bulk Upload Request.',
        error: error as ResponseError,
      };
      setNotification(notificationData);
      setSubmitting(false);
    }
  };

  // Submit the Bulk Upload
  const onSubmitClick = (): void => {
    Logging.Debug('Submit', leftDropFiles, leftStatusMap, rightDropFiles, rightStatusMap);

    // Get the Valid Files
    const validLeftFiles: File[] = getValidFiles(leftDropFiles, leftStatusMap);
    const validRightFiles: File[] = getValidFiles(rightDropFiles, rightStatusMap);

    // Prevent multiple Submits
    setSubmitting(true);

    dispatch(submitBulkUpload(validLeftFiles, validRightFiles));
  };

  // Csv Drop Zone Parameters
  const csvDropProps: DropZoneProps = {
    dragTextDescription: 'Drag the .CSV file here, or click to select file.',
    dropTextDescription: 'Drop the .CSV file here.',
    showRestrictions: true,
    maxFileSize: BYTES_PER_MEGABYTE * 30, // 30MB
    maxFilesSize: null,
    maxNumberFiles: 1,
    csvLimits: [{
      column: 'Correlation ID',
      check: 'Maximum',
      occurrences: 5000,
      error: 'Maximum of 5,000 certifications are allowed.',
      showInRestrictions: true,
    },
    {
      column: 'Correlation ID',
      check: 'Minimum',
      occurrences: 1,
      error: 'Minimum of 1 certification is required.',
      showInRestrictions: false,
    }],
    allowedFileTypes: [
      FileType.Csv, // Chrome
      FileType.Csv2, // Safari
    ],
    dragDropImage: '/images/drag-drop-csv.png',
    dropFiles: leftDropFiles,
    setDropFiles: setLeftDropFiles,
    statusMap: leftStatusMap,
    setStatusMap: setLeftStatusMap,
    setFilesError: setLeftFilesError,
  };

  // Zip Drop Zone Parameters
  const zipDropProps: DropZoneProps = {
    dragTextDescription: 'Drag the Certificate Image file(s) here, or click to select files.',
    dropTextDescription: 'Drop the Certificate Image file(s) here.',
    showRestrictions: true,
    maxFileSize: BYTES_PER_MEGABYTE * 950, // 950 MB
    maxFilesSize: BYTES_PER_GIGABYTE * 1, // 1 GB
    maxNumberFiles: 1000,
    allowedFileTypes: [
      FileType.Zip, // Chrome
      FileType.Zip2, // Safari
      FileType.Pdf,
      FileType.Png,
      FileType.Gif,
      FileType.Jpg,
    ],
    dragDropImage: '/images/drag-drop-zip.png',
    dropFiles: rightDropFiles,
    setDropFiles: setRightDropFiles,
    statusMap: rightStatusMap,
    setStatusMap: setRightStatusMap,
    setFilesError: setRightFilesError,
    // virusScanningUrl: 'http://virus-farga-10kraep1awo13-329400583.us-east-1.elb.amazonaws.com/v1/syncScan/file',
  };

  React.useEffect(() => {
    // This Code will run once after the initial Render
    Logging.Debug('BulkUpload - Initial Render');

    return (): void => {
      // This Code will run once the component unmounts.
      Logging.Debug('BulkUpload - Unmounts');
      setSubmitting(false);
    };
  }, []);

  const showRightDrop = leftDropFiles.length > 0;
  const submitDisabled = (getValidFiles(leftDropFiles, leftStatusMap).length === 0)
    || (getInvalidFiles(leftDropFiles, leftStatusMap).length > 0)
    || (getInvalidFiles(rightDropFiles, rightStatusMap).length > 0)
    || (numberUploads >= maxNumberUploads)
    || submitting
    || leftFilesError
    || rightFilesError;

  if (redirect) {
    return (
      <Redirect to={redirect} />
    );
  }

  const Spacer = () => <span style={{ marginRight: '10px' }} />;

  const maxUploadMessage = (maxNumberUploads === 1)
    ? 'A maximum of 1 simultaneous Bulk Upload Transfer is allowed.  Please wait until the current transfer completes.'
    : `A maximum of ${maxNumberUploads} simultaneous Bulk Upload Transfers are allowed.  Please wait until some of the current transfers complete.`;

  const rightFilesSize: number = getFilesSize(rightDropFiles);
  const leftHeading: string = leftDropFiles.length > 0 ? `${leftDropFiles.length} Tabular Data File (.CSV)` : 'Tabular Data File (.CSV)';
  const rightHeading: string = rightDropFiles.length > 0
    ? `${rightDropFiles.length} Certificate Images File(s) - ${formatFileSize(rightFilesSize)}`
    : 'Certificate Images File(s)';

  return (
    <div className="buContainer">
      <div className="buHeader">
        <div className="buHeaderBackToCertificates">
          <VertexLink href="#/certificates" display="Back to Certificates" />
        </div>
        <div className="buHeaderMain">
          <div className="buHeaderMainLeft">
            <h2>
              <span>Bulk Upload</span>
            </h2>
          </div>
          <div className="buHeaderMainRight">
            <a href="#/bulkupload-history" title="Bulk Upload History">
              <VtxButton
                id="bulkUploadHistoryButton"
                type="default"
              >
                Bulk Upload History
              </VtxButton>
            </a>
          </div>
        </div>
        <div className="buHeaderHint">
          {
            (numberUploads >= maxNumberUploads)
              ? (
                <span className="redText">{maxUploadMessage}</span>
              )
              : (
                <>
                  <span>Choose your .CSV file and Certificate Image file(s) you want to import.&nbsp;
                    <a download href={ECM_UPLOAD_TEMPLATE}>Download .CSV Template</a>
                  </span>
                  <span>Please refer to the following document for valid jurisdictions, exception reasons and other specific criteria.&nbsp;
                    <a download href={ECM_UPLOAD_REFERENCE}>Download Reference File</a>
                  </span>
                </>
              )
          }
        </div>
      </div>
      <div className="buContentContainer">
        <div className="buContent">
          <div className="buContentHeading">{leftHeading}</div>
          <DropZone {...csvDropProps} />
          <div className="buContentFooter">
            {
              leftFilesError
              && (
                <>
                  <VtxIcon type="exclamation-circle" fontSize={20} color="red" className="buContentFilesIcons" />
                  <span>{leftFilesError}</span>
                </>
              )
            }
          </div>
        </div>
        <div className="buContentSpacer" />
        <div className={showRightDrop ? 'buContent' : 'buContent buContentHidden'}>
          <div className="buContentHeading">{rightHeading}</div>
          <DropZone {...zipDropProps} />
          <div className="buContentFooter">
            {
              rightFilesError
              && (
                <>
                  <VtxIcon type="exclamation-circle" fontSize={20} color="red" className="buContentFilesIcons" />
                  <span>{rightFilesError}</span>
                </>
              )
            }
          </div>
        </div>
      </div>
      <div className="buFooter">
        <VtxButton
          id="clearAllButton"
          type="default"
          onClick={onClearAllClick}
        >
          Clear All
        </VtxButton>
        <Spacer />
        <VtxButton
          id="submitButton"
          type="primary"
          disabled={submitDisabled}
          onClick={onSubmitClick}
        >
          Submit
        </VtxButton>
      </div>
      <Notification
        data={notification}
        onNotificationClose={onNotificationClose}
        autoClose
      />
    </div>
  );
};

export default BulkUpload;
